// GlassLoginForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  LogIn, 
  Users,
  Shield,
  ArrowLeft,
  Building2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import axios from 'axios';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  // userRole state removed because it was not used elsewhere
  const navigate = useNavigate();

  // Configure axios instance using CRA env var
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  // Add token to requests if exists
  api.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Get user role after login
  // memoized because it is used in useEffect
  const getUserRole = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await api.get('/role');
      // Redirect based on role
      if (response.data.role === 'admin') {
        navigate('/admin');
      } else if (response.data.role === 'user') {
        navigate('/user/dashboard');
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
  }, [api, navigate]);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      getUserRole();
    }
  }, [getUserRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Send login request
      const response = await api.post('/login', {
        email: formData.email,
        password: formData.password
      });

      // Check if login was successful
      if (response.status === 200 || response.status === 201) {
        // Save token if exists
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        

        // Show success message
        await Swal.fire({
          icon: 'success',
          title: 'Login Successful!',
          text: response.data.message || 'Welcome back!',
          confirmButtonColor: '#7c3aed',
          timer: 2000,
          timerProgressBar: true,
        });

        // Get user role and redirect
        await getUserRole();

        // If getUserRole doesn't redirect, redirect based on user data
        if (response.data.user?.role) {
          if (response.data.user.role === 'admin') {
            navigate('/admin/dashboard');
          } else if (response.data.user.role === 'user') {
            navigate('/user/dashboard');
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response) {
        if (error.response.status === 422) {
          // Validation errors
          const errors = error.response.data.errors;
          if (errors) {
            errorMessage = Object.values(errors).flat().join(', ');
          } else {
            errorMessage = error.response.data.message || 'Validation failed';
          }
        } else if (error.response.status === 401) {
          errorMessage = 'Invalid email or password.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      }

      // Show error message
      await Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: errorMessage,
        confirmButtonColor: '#ef4444',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Demo login function (optional - remove in production)
  const handleDemoLogin = async (role = 'user') => {
    setLoading(true);
    
    // Simulate API call
    setTimeout(async () => {
      // For demo purposes - create a mock user
      const demoUsers = {
        admin: {
          email: 'admin@farrahiyyumma.org',
          token: 'demo_admin_token_123',
          user: { id: 1, name: 'Admin User', email: 'admin@farrahiyyumma.org', role: 'admin' }
        },
        user: {
          email: 'user@farrahiyyumma.org',
          token: 'demo_user_token_123',
          user: { id: 2, name: 'Regular User', email: 'user@farrahiyyumma.org', role: 'user' }
        }
      };
      
      const demoUser = demoUsers[role];
      
      // Save to localStorage
      localStorage.setItem('token', demoUser.token);
      localStorage.setItem('user', JSON.stringify(demoUser.user));
      
      // Show success message
      await Swal.fire({
        icon: 'success',
        title: 'Demo Login Successful!',
        text: `Logged in as ${role} user`,
        confirmButtonColor: '#7c3aed',
        timer: 1500,
        timerProgressBar: true,
      });
      
      // Redirect
      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/user/dashboard');
      }
      
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="flex items-center justify-center py-16 bg-gradient-to-br from-teal-400 to-blue-500">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-60 h-60 bg-sky-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl"></div>
      </div>

      {/* Back Button */}
      <a 
        href="/" 
        className="absolute top-8 left-8 z-10 flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl text-white hover:bg-white/20 transition-all"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back to Home</span>
      </a>

      <div className="relative w-full max-w-lg">
        {/* Decorative Elements */}
        <div className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-br from-indigo-500 to-sky-500 rounded-3xl opacity-20 blur-xl"></div>
        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-tr from-emerald-500 to-sky-500 rounded-3xl opacity-20 blur-xl"></div>

        {/* Main Card */}
        <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Gradient Top Bar */}
          <div className="h-2 bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500"></div>
          
          <div className="p-6">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-indigo-500/20 to-sky-500/20 backdrop-blur-sm rounded-3xl border border-white/20 mb-6">
                <Building2 className="h-12 w-12 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-sky-200 bg-clip-text text-transparent">
                Welcome Back to Fara Hiyuma 
              </h1>
              <p className="text-white/60">Access your Farra Hiyyummaa account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div className="group">
                <div className="flex items-center space-x-2 mb-3">
                  <Mail className="h-5 w-5 text-sky-400" />
                  <label className="text-sm font-medium text-white/80">
                    Member Email
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-5 py-3 bg-white text-black border border-gray-300 rounded-2xl shadow-md placeholder-black/40 focus:outline-none focus:bg-white focus:border-sky-400 transition-all duration-300"
                    placeholder="Enter your registered email"
                    required
                    disabled={loading}
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-400/50 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>

              {/* Password Field */}
              <div className="group">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Lock className="h-5 w-5 text-sky-400" />
                    <label className="text-sm font-medium text-white/80">
                      Password
                    </label>
                  </div>
                  <a 
                    href="/forgot-password" 
                    className="text-sm text-sky-300 hover:text-sky-200 transition-colors"
                  >
                    Forgot Password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-5 py-3 bg-white text-black border border-gray-300 rounded-2xl shadow-md placeholder-black/40 focus:outline-none focus:bg-white focus:border-sky-400 transition-all duration-300 pr-12"
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-lg hover:bg-white/10 transition-colors"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-sky-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-sky-400" />
                    )}
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-400/50 to-transparent opacity-0 group-focus-within:opacity-100 transition-opacity"></div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full group relative overflow-hidden bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-500 text-white font-semibold py-4 px-6 rounded-2xl hover:shadow-2xl transition-all duration-500 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-sky-600 to-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative flex items-center justify-center space-x-3">
                  {loading ? (
                    <>
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-lg">Signing In...</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="h-5 w-5" />
                      <span className="text-lg">Sign In to Dashboard</span>
                    </>
                  )}
                </div>
                <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shine transition-all duration-1000"></div>
              </button>
            </form>

            {/* Demo Login Buttons (Remove in production) */}
       
            {/* Divider */}
        
            {/* Registration CTA */}
            
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-white/10 bg-white/5 backdrop-blur-sm">
            <div className="flex items-center justify-center space-x-4 text-white/60 text-sm">
              <div className="flex items-center space-x-1">
                <Shield className="h-4 w-4 text-emerald-400" />
                <span>Secure</span>
              </div>
              <span>•</span>
              <span>© 2024 Farra Hiyyumma</span>
              <span>•</span>
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;